import { useState, useRef, useEffect } from "react";
import { useCommunityAuth } from "../../contexts/CommunityAuthContext";
import { Formik, Form, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faShieldAlt,
  faClipboardList,
  faCheck,
  faArrowRight,
  faArrowLeft,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { InlineSpinner } from "../../componentes/LoadingIndicator";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import FormField from "./FormField";
import ReCaptcha from "../../componentes/ReCaptcha";
import SuccessNotification from "../../componentes/SuccessNotification";
import { abuseValidationSchema } from "./validationSchemas";
import {
  abuseTypes,
  abusePlatformTypes,
  relationshipToVictim,
  abuseUrgencyLevels,
} from "./formConfig";
import { sanitizeFormData } from "../../utils/security";
import { handleSubmissionError } from "../../utils/errorHandling";
import { API_BASE_URL } from '../../api/config';

const baseUrl = API_BASE_URL;

const ScrollToError = () => {
  const { submitCount, errors } = useFormikContext();
  const prevSubmitCount = useRef(submitCount);

  useEffect(() => {
    if (
      submitCount > prevSubmitCount.current &&
      Object.keys(errors).length > 0
    ) {
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
    prevSubmitCount.current = submitCount;
  }, [submitCount, errors]);

  return null;
};

const SteppedAbuseForm = () => {
  const { user } = useCommunityAuth();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaRef, setRecaptchaRef] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedTrackingId, setSubmittedTrackingId] = useState('');

  const steps = [
    {
      id: "reporter",
      title: t("abuse.steps.reporter_info"),
      icon: faUser,
      fields: ["full_name", "email", "phone_number", "organization", "relationship_to_victim"]
    },
    {
      id: "abuse",
      title: t("abuse.steps.abuse_details"),
      icon: faShieldAlt,
      fields: ["abuse_type", "platform_type", "platform_name", "incident_url", "perpetrator_username", "victim_username", "incident_date", "is_ongoing"]
    },
    {
      id: "evidence",
      title: t("abuse.steps.evidence_impact"),
      icon: faClipboardList,
      fields: ["abuse_description", "evidence_description", "impact_on_victim", "additional_notes", "urgency_level", "evidence_files"]
    }
  ];

  const initialValues = {
    full_name: user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : "",
    email: user?.email || "",
    phone_number: user?.phone || "",
    organization: "",
    relationship_to_victim: "",
    abuse_type: "",
    other_abuse_type: "",
    platform_type: "",
    platform_name: "",
    incident_url: "",
    perpetrator_username: "",
    victim_username: "",
    incident_date: "",
    is_ongoing: false,
    abuse_description: "",
    evidence_description: "",
    impact_on_victim: "",
    additional_notes: "",
    urgency_level: "",
    evidence_files: [],
    recaptcha: "",
  };

  const handleNext = async (validateForm, values, setTouched) => {
    if (currentStep < steps.length - 1) {
      // Get current step fields
      const currentStepFields = steps[currentStep].fields;

      // Create touched object for current step fields
      const touchedFields = {};
      currentStepFields.forEach(field => {
        touchedFields[field] = true;
      });

      // Set touched for current step fields
      setTouched(touchedFields);

      // Validate the form
      const errors = await validateForm();

      // Check if current step has any validation errors
      const currentStepErrors = currentStepFields.some(field => {
        return errors[field];
      });

      if (!currentStepErrors) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Scroll to first error field
        const firstErrorField = currentStepFields.find(field => errors[field]);
        if (firstErrorField) {
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.focus();
          }
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (
    values,
    { resetForm, setErrors, setSubmitting }
  ) => {
    try {
      setIsSubmitting(true);
      if (!values.recaptcha) {
        toast.error(t("incident report.ui.recaptcha_error"));
        return;
      }

      const { sanitized, issues } = sanitizeFormData(values);

      if (issues.length > 0) {
        toast.error("Security issues detected in form data");
        setErrors(
          issues.reduce(
            (acc, issue) => ({
              ...acc,
              [issue.field]: `Security issue: ${issue.issues.join(", ")}`,
            }),
            {}
          )
        );
        return;
      }

      const formData = new FormData();
      Object.entries(sanitized).forEach(([key, value]) => {
        if (key === "evidence_files" && value) {
          Array.from(value).forEach((file) => formData.append("evidence_files", file));
        } else if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      // Get CSRF token from cookie
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(csrfToken && { "X-CSRFToken": csrfToken })
        },
        withCredentials: true
      };

      const response = await axios.post(
        `${baseUrl}/api/incident-reports/abuse/create/`,
        formData,
        config
      );

      if (response.data.status === "success") {
        setSubmittedEmail(values.email);
        setSubmittedTrackingId(response.data.tracking_id || 'Not available');
        setShowSuccess(true);
        resetForm({ values: { ...initialValues, evidence_files: null, recaptcha: "" } });
        const fileInput = document.getElementById("evidence_files");
        if (fileInput) fileInput.value = "";
        if (recaptchaRef) recaptchaRef.reset();
        setSubmitting(false);
        setCurrentStep(0);
      }
    } catch (error) {
      handleSubmissionError(error, setErrors, toast, t);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setCurrentStep(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderStepContent = (step, values) => {
    switch (step.id) {
      case "reporter":
        return (
          <div className="space-y-6">
            <FormField
              name="full_name"
              label={t("abuse.fields.full_name")}
              placeholder={t("abuse.fields.full_name_placeholder")}
            />
            <FormField
              name="email"
              label={t("incident report.abuse.fields.email")}
              type="email"
              placeholder={t("incident report.abuse.fields.email_placeholder")}
            />
            <FormField
              name="phone_number"
              label={t("incident report.abuse.fields.phone_number")}
              placeholder={t("incident report.abuse.fields.phone_number_placeholder")}
            />
            {/* <FormField
              name="organization"
              label={t("abuse.fields.organization")}
              placeholder={t("abuse.fields.organization_placeholder")}
            /> */}
            <FormField
              name="relationship_to_victim"
              label={t("abuse.fields.relationship_to_victim")}
              as="select"
              options={relationshipToVictim}
              required
            />
          </div>
        );
      case "abuse":
        return (
          <div className="space-y-6">
            <FormField
              name="abuse_type"
              label={t("abuse.fields.abuse_type")}
              as="select"
              options={abuseTypes}
              required
            />
            {values.abuse_type === "other" && (
              <FormField
                name="other_abuse_type"
                label={t("abuse.fields.other_abuse_type")}
                placeholder={t("abuse.fields.other_abuse_type_placeholder")}
                required
              />
            )}
            <FormField
              name="platform_type"
              label={t("abuse.fields.platform_type")}
              as="select"
              options={abusePlatformTypes}
              required
            />
            <FormField
              name="platform_name"
              label={t("abuse.fields.platform_name")}
              placeholder={t("abuse.fields.platform_name_placeholder")}
            />
            <FormField
              name="incident_url"
              label={t("abuse.fields.incident_url")}
              placeholder={t("abuse.fields.incident_url_placeholder")}
              required
            />
            <FormField
              name="perpetrator_username"
              label={t("abuse.fields.perpetrator_username")}
              placeholder={t("abuse.fields.perpetrator_username_placeholder")}
            />
            <FormField
              name="victim_username"
              label={t("abuse.fields.victim_username")}
              placeholder={t("abuse.fields.victim_username_placeholder")}
            />
            <FormField
              name="incident_date"
              label={t("abuse.fields.incident_date")}
              type="date"
              required
            />
            <FormField
              name="is_ongoing"
              label={t("abuse.fields.is_ongoing")}
              type="checkbox"
              options={[{ id: "is_ongoing", name: t("abuse.fields.is_ongoing_option") }]}
            />
          </div>
        );
      case "evidence":
        return (
          <div className="space-y-6">
            <FormField
              name="abuse_description"
              label={t("abuse.fields.abuse_description")}
              as="textarea"
              placeholder={t("abuse.fields.abuse_description_placeholder")}
              required
            />
            <FormField
              name="evidence_description"
              label={t("abuse.fields.evidence_description")}
              as="textarea"
              placeholder={t("abuse.fields.evidence_description_placeholder")}
            />
            <FormField
              name="impact_on_victim"
              label={t("abuse.fields.impact_on_victim")}
              as="textarea"
              placeholder={t("abuse.fields.impact_on_victim_placeholder")}
            />
            <FormField
              name="additional_notes"
              label={t("abuse.fields.additional_notes")}
              as="textarea"
              placeholder={t("abuse.fields.additional_notes_placeholder")}
            />
            <FormField
              name="urgency_level"
              label={t("abuse.fields.urgency_level")}
              as="select"
              options={abuseUrgencyLevels}
              required
            />

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
              <div className="flex">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-blue-400 mr-3 mt-1" />
                <div>
                  <p className="font-semibold text-blue-800 mb-1">{t("abuse.evidence_notice.title")}</p>
                  <p className="text-blue-700 text-sm">
                    {t("abuse.evidence_notice.text")}
                  </p>
                </div>
              </div>
            </div>

            <FormField
              name="evidence_files"
              label={t("abuse.fields.evidence_files")}
              type="file"
              multiple
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {showSuccess && (
        <SuccessNotification
          email={submittedEmail}
          trackingId={submittedTrackingId}
          onClose={handleCloseSuccess}
        />
      )}
      <PageHeader />
      <GradientBanner
        title={t("reportLanding.reportTypes.onlineAbuse.title")}
        description={t("reportLanding.reportTypes.onlineAbuse.description")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.report_incident"), link: "/incident-report" },
          { label: t("reportLanding.reportTypes.onlineAbuse.title"), link: null },
        ]}
        backgroundIcons={[faExclamationTriangle, faUser, faShieldAlt, faClipboardList]}
      />
      <div className="min-h-screen bg-gray-50 py-8 font-nunito-sans">
        <div className="px-dynamic">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Sidebar - Steps */}
            <div className="hidden lg:block lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-4 max-h-screen overflow-visible">
                <h2 className="text-xl font-bold text-gray-800 mb-6">{t("incident report.ui.report_progress")}</h2>
                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`flex items-center p-4 rounded-lg transition-all cursor-pointer ${index === currentStep
                          ? "bg-primary text-white shadow-md"
                          : index < currentStep
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-gray-50 text-gray-500"
                        }`}
                      onClick={() => setCurrentStep(index)}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${index === currentStep
                          ? "bg-white/20"
                          : index < currentStep
                            ? "bg-green-100"
                            : "bg-gray-200"
                        }`}>
                        <FontAwesomeIcon
                          icon={index < currentStep ? faCheck : step.icon}
                          className={`${index === currentStep
                              ? "text-white"
                              : index < currentStep
                                ? "text-green-600"
                                : "text-gray-400"
                            }`}
                        />
                      </div>
                      <div>
                        <div className="font-medium">{step.title}</div>
                        <div className={`text-sm ${index === currentStep
                            ? "text-white/80"
                            : index < currentStep
                              ? "text-green-600"
                              : "text-gray-400"
                          }`}>
                          {t("incident report.ui.step_of", { current: index + 1, total: steps.length })}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Content - Form */}
            <div className="flex-1">
              <Formik
                initialValues={initialValues}
                validationSchema={abuseValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
              >
                {({ values, setFieldValue, validateForm, setTouched }) => (
                  <Form>
                    <ScrollToError />
                    <motion.div
                      key={currentStep}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-white rounded-2xl shadow-lg p-8"
                    >
                      <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">
                          {steps[currentStep].title}
                        </h1>
                        <p className="text-gray-600">
                          {t("incident report.ui.step_of", { current: currentStep + 1, total: steps.length })} - {t("incident report.ui.complete_form")}
                        </p>
                      </div>

                      {renderStepContent(steps[currentStep], values)}

                      {/* reCAPTCHA - Show only on last step */}
                      {currentStep === steps.length - 1 && (
                        <div className="mt-6">
                          <ReCaptcha
                            ref={(ref) => setRecaptchaRef(ref)}
                            onChange={(token) => setFieldValue('recaptcha', token)}
                            onExpired={() => setFieldValue('recaptcha', '')}
                          />
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div className="flex justify-between gap-3 mt-8 pt-6 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={handlePrevious}
                          disabled={currentStep === 0}
                          className={`flex flex-1 sm:flex-none justify-center sm:justify-start items-center px-6 py-3 rounded-lg font-medium transition-all ${currentStep === 0
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        >
                          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                          {t("incident report.ui.previous")}
                        </button>

                        {currentStep === steps.length - 1 ? (
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex flex-1 sm:flex-none justify-center sm:justify-start items-center px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSubmitting ? (
                              <>
                                <InlineSpinner size="sm" className="mr-2 border-white/30 border-t-white" />
                                {t("incident report.ui.submitting")}
                              </>
                            ) : (
                              <>
                                {t("incident report.ui.submit")}
                                <FontAwesomeIcon icon={faCheck} className="ml-2" />
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleNext(validateForm, values, setTouched)}
                            className="flex flex-1 sm:flex-none justify-center sm:justify-start items-center px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all"
                          >
                            {t("incident report.ui.next")}
                            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
      <Toaster position="top-right" />
    </>
  );
};

export default SteppedAbuseForm;
